import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { SnackbarProvider } from 'notistack';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { HelmetProvider } from 'react-helmet-async';
import { Route, Routes } from 'react-router-dom';

import CookieConsent, { getCookieConsentValue } from 'react-cookie-consent';

import Login from './authentication/Login';
import NewProject from './project/NewProject';
import { ProjectForm, ScrollToTopOnce } from './project/Project';

import Feedback from './authentication/Feedback';

import { ThemeProvider } from '@mui/material';
import './App.css';
import AuthenticationGuard from './authentication/AuthenticationGuard';
import { AuthenticationProvider } from './authentication/context/useAuthentication';
import Home from './authentication/Home';
import Recover from './authentication/Recover';
import Register from './authentication/Register';
import ResetPassword from './authentication/ResetPassword';
import Dashboard from './dashboard';
import ProjectTypeList from './dashboard/ProjectTypeList';
import AppLayout from './layout/AppLayout';
import { HeaderProject } from './layout/Header';
import { Provider as BackToProvider } from './project/context/useBackTo';
import { ValidationProvider } from './project/context/useValidation';

import AnnualReports from './annual-reports';
import HeaderAnnualReports from './annual-reports/Header';
import AnnualReport from './annual-reports/Report';
import AnnualReportProjectResults from './annual-reports/ReportProjectResults';

import CncReports from './cnc-reports';
import HeaderCncReports from './cnc-reports/Header';
import CncReport from './cnc-reports/Report';

import AnnualReportPdf from './annual-reports/pdf/AnnualReportPdf';
import Checkout from './checkout';
import CheckoutStatus from './checkout/CheckoutStatus';
import ProjectPdf from './project/pdf/ProjectPdf';
import Results from './results/Results';
import Subscription from './subscription/Subscription';
import theme from './theme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if ((error as any).message === 'Unauthorized') {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  useEffect(() => {
    if (getCookieConsentValue()) {
      initSentry();
    }
  }, []);

  const initSentry = () => {
    if (process.env.REACT_APP_SENTRY_DSN) {
      Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        integrations: [new BrowserTracing()],
        tracesSampleRate: 1.0,
        sendDefaultPii: true,
      });
    }
  };

  const handleAccept = () => {
    initSentry();
  };
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CookieConsent
          onAccept={handleAccept}
          flipButtons
          enableDeclineButton
          location="bottom"
          buttonText="Autoriser et fermer"
          declineButtonText="Refuser"
          expires={150}
        >
          JYROS ne fait <b>aucun suivi publicitaire</b> et ne collecte aucune
          données personnelles, hors données d'authentification. Des cookies sont
          utilisés à des fins statistiques ou de fonctionnement, ainsi que
          d'analyse (que vous pouvez refuser ici), nous permettant d'améliorer le
          site en continu.
        </CookieConsent>
        <QueryClientProvider client={queryClient}>
        <BackToProvider>
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            style={{ top: '64px' }}
            className="snackbar"
          >
            <AuthenticationProvider>
              <AuthenticationGuard suppressError>
                <Feedback />
              </AuthenticationGuard>
              <Routes>
                <Route path="/authentication" element={<Home />} />
                <Route path="/authentication/login" element={<Login />} />
                <Route path="/authentication/register" element={<Register />} />
                <Route path="/authentication/recover" element={<Recover />} />
                <Route
                  path="/authentication/recover/:email"
                  element={<Recover />}
                />
                <Route
                  path="/authentication/reset-password/:token"
                  element={
                    <ResetPassword
                      title="Réinitialisation mot de passe"
                      fieldLabel="Nouveau mot de passe"
                      successMessage="Mot de passe réinitialisé avec succès"
                      buttonLabel="Réinitialiser"
                    />
                  }
                />
                <Route
                  path="/authentication/confirm-account/:token"
                  element={
                    <ResetPassword
                      title="Création de compte"
                      fieldLabel="Mot de passe"
                      successMessage="Compte créé avec succès"
                      buttonLabel="Créer"
                    />
                  }
                />

                <Route
                  path="/"
                  element={
                    <AuthenticationGuard>
                      <Dashboard />
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/checkout/"
                  element={
                    <AuthenticationGuard>
                      <Checkout />
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="/checkout/status"
                  element={
                    <AuthenticationGuard>
                      <CheckoutStatus />
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/projects/:type/"
                  element={
                    <AuthenticationGuard>
                      <ProjectTypeList />
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/subscription/"
                  element={
                    <AuthenticationGuard>
                      <Subscription />
                    </AuthenticationGuard>
                  }
                />

                {/* CNC REPORTS */}

                <Route
                  path="/cnc-reports"
                  element={
                    <AuthenticationGuard>
                      <AppLayout bgColor="white" header={<HeaderCncReports />}>
                        <ScrollToTopOnce />
                        <CncReports />
                      </AppLayout>
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/cnc-reports/:reportId"
                  element={
                    <AuthenticationGuard>
                      <AppLayout header={<HeaderCncReports />}>
                        <ScrollToTopOnce />
                        <CncReport />
                      </AppLayout>
                    </AuthenticationGuard>
                  }
                />

                {/* ANNUAL REPORTS */}

                <Route
                  path="/annual-reports/:reportId/pdf"
                  element={
                    <AuthenticationGuard>
                      <AnnualReportPdf />
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/annual-reports/:reportId/:projectId"
                  element={
                    <AuthenticationGuard>
                      <AppLayout header={<HeaderAnnualReports />}>
                        <ScrollToTopOnce />
                        <AnnualReportProjectResults />
                      </AppLayout>
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/annual-reports"
                  element={
                    <AuthenticationGuard>
                      <AppLayout
                        bgColor="white"
                        header={<HeaderAnnualReports />}
                      >
                        <ScrollToTopOnce />
                        <AnnualReports />
                      </AppLayout>
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/annual-reports/:reportId"
                  element={
                    <AuthenticationGuard>
                      <AppLayout header={<HeaderAnnualReports />}>
                        <ScrollToTopOnce />
                        <AnnualReport />
                      </AppLayout>
                    </AuthenticationGuard>
                  }
                />

                {/* PROJECTS */}

                <Route
                  path="/project/:projectId/"
                  element={
                    <AuthenticationGuard>
                      <ValidationProvider>
                        <AppLayout header={<HeaderProject />}>
                          <ProjectForm />
                        </AppLayout>
                      </ValidationProvider>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="/project/:projectId/pdf"
                  element={
                    <AuthenticationGuard>
                      <ProjectPdf />
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="/project/:projectId/results"
                  element={
                    <AuthenticationGuard>
                      <ValidationProvider>
                        <AppLayout header={<HeaderProject />}>
                          <ScrollToTopOnce />
                          <Results />
                        </AppLayout>
                      </ValidationProvider>
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/project"
                  element={
                    <AuthenticationGuard>
                      <NewProject />
                    </AuthenticationGuard>
                  }
                />
              </Routes>
            </AuthenticationProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </SnackbarProvider>
        </BackToProvider>
      </QueryClientProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
